import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog'
import { MatAutocompleteModule } from '@angular/material/autocomplete'
import { MatButton, MatIconButton } from '@angular/material/button'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInput } from '@angular/material/input'
import { MatOption, MatSelect } from '@angular/material/select'
import { MatTooltip } from '@angular/material/tooltip'
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core'
import { map, Observable, tap } from 'rxjs'
import { CALL_ACCESS_ROLES, CallAccessRole, CallsService, ClientCallAccess, filterOfferedAccessLevels } from '../../calls.service'
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component'
import { SpinnerComponent } from '../../spinner/spinner.component'
import { ToastService } from '../../toast.service'
import { User, UsersService } from '../../users.service'
import { constructNameForUser } from '../../utils'

export type ManageCallUsersDialogData = {
  callId: number
  callName: string
  organizationId: number
  access: ClientCallAccess[]
  allowRestrictedUsers: boolean
}

/**
 * Puts one row back where it was after a refused removal.
 *
 * Only that row: another add or remove may have landed while the request was out, so restoring the
 * list as it stood when it went out would undo whatever happened in between. A row that is already
 * back is left alone, and an index the list has since shrunk past lands at the end.
 */
export function restoreCallUser(people: ClientCallAccess[], person: ClientCallAccess, index: number): ClientCallAccess[] {
  if (people.some((other) => other.userId === person.userId)) {
    return people
  }
  const restored = [...people]
  restored.splice(Math.min(Math.max(index, 0), restored.length), 0, person)
  return restored
}

/**
 * Puts one person's role back after a refused change, on the same terms as {@link restoreCallUser}.
 *
 * A second change may have landed while the first was still out, and that one is the reader's most
 * recent intent, so the role is only put back where the row still reads as the one that was refused.
 */
export function restoreCallUserRole(people: ClientCallAccess[], userId: number, refusedRole: CallAccessRole, previousRole: CallAccessRole): ClientCallAccess[] {
  return people.map((person) => (person.userId === userId && person.role === refusedRole ? { ...person, role: previousRole } : person))
}

@Component({
  selector: 'app-manage-call-users-dialog',
  standalone: true,
  imports: [
    AsyncPipe,
    FormsModule,
    MatAutocompleteModule,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatFormField,
    MatIcon,
    MatIconButton,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatTooltip,
    SpinnerComponent,
    TranslateDirective,
    TranslatePipe,
  ],
  templateUrl: './manage-call-users-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './manage-call-users-dialog.component.scss',
})
export class ManageCallUsersDialogComponent {
  data: ManageCallUsersDialogData = inject<ManageCallUsersDialogData>(MAT_DIALOG_DATA)
  callsService = inject(CallsService)
  usersService = inject(UsersService)
  translateService = inject(TranslateService)
  toastService = inject(ToastService)
  dialog = inject(MatDialog)

  setCallAccessMutation = this.callsService.setCallAccessMutation()
  removeCallAccessMutation = this.callsService.removeCallAccessMutation()

  readonly roles = filterOfferedAccessLevels(CALL_ACCESS_ROLES, this.data.allowRestrictedUsers)

  people: ClientCallAccess[] = [...this.data.access]

  /** The autocomplete's text, which matches one option exactly once a user has been picked. */
  filter = ''

  /** What a newly added person is made, which is what the list was only ever able to add before. */
  roleToAdd: CallAccessRole = 'OWNER'

  private currentUserId = this.usersService.currentUser$.value?.id ?? null
  private organizationUsers: User[] = []

  // A support user's own organization has nothing to do with the meeting, so they are offered the
  // people of the meeting's organization instead.
  usersResult$: Observable<{ isLoading: boolean }> =
    this.usersService.currentUser$.value?.role === 'SUPPORT'
      ? this.usersService.getUsersByOrganizationId(this.data.organizationId).pipe(tap((result) => this.keepUsers(result.isSuccess, result.data?.list ?? null)))
      : this.usersService.getOrganizationUsers().pipe(tap((result) => this.keepUsers(result.isSuccess, result.data ?? null)))

  /** Null while the reader is still typing, so the button only enables once an option is picked. */
  get selectedUser(): User | null {
    return this.addableUsers.find((user) => this.describeUser(user) === this.filter) ?? null
  }

  // The acting administrator is dropped from the options: granting themselves access is refused by
  // the backend, so offering it would only produce an error.
  get addableUsers(): User[] {
    const filter = this.filter.toLowerCase()
    return this.organizationUsers
      .filter((user) => user.id !== this.currentUserId && !this.people.some((person) => person.userId === user.id))
      .filter((user) => filter === '' || this.describeUser(user).toLowerCase().includes(filter))
  }

  private keepUsers(isSuccess: boolean, users: User[] | null) {
    if (isSuccess && users !== null) {
      this.organizationUsers = users
    }
  }

  // Doubles as the option's value, so that the picked option is recognisable from the text alone.
  describeUser(user: User): string {
    return `${constructNameForUser(user)} (${user.email})`
  }

  roleLabel(role: CallAccessRole): string {
    return `CALLS.ADMIN.MANAGE_CALL_USERS.ROLES.${role}`
  }

  // Shown before the request has been answered, so the list moves as fast as the click. The request
  // is what makes it true, and a refusal takes the row back out.
  async addUser() {
    const user = this.selectedUser
    if (user === null) {
      return
    }
    const role = this.roleToAdd
    const added: ClientCallAccess = { userId: user.id, firstName: user.firstName, lastName: user.lastName, email: user.email, role, isSetToJoin: false }
    this.people = [...this.people, added]
    // Derived from the filter, so clearing that clears the selection too.
    this.filter = ''
    try {
      await this.setCallAccessMutation.mutateAsync({ callId: this.data.callId, userId: user.id, role })
      this.toastService.show('CALLS.ADMIN.ADD_CALL_USER.SUCCESS')
    } catch {
      this.people = this.people.filter((person) => person.userId !== added.userId)
    }
  }

  // Picking from the dropdown is the change: there is no separate save, so the row reads as the new
  // role at once and a refusal puts the old one back.
  async changeRole(person: ClientCallAccess, role: CallAccessRole) {
    const previousRole = person.role
    if (role === previousRole) {
      return
    }
    this.people = this.people.map((other) => (other.userId === person.userId ? { ...other, role } : other))
    try {
      await this.setCallAccessMutation.mutateAsync({ callId: this.data.callId, userId: person.userId, role })
      this.toastService.show('CALLS.ADMIN.MANAGE_CALL_USERS.ROLE_CHANGED')
    } catch {
      this.people = restoreCallUserRole(this.people, person.userId, role, previousRole)
    }
  }

  removeUser(person: ClientCallAccess) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('CALLS.ADMIN.REVOKE_USER_ACCESS.TITLE'),
        description: this.translateService.instant('CALLS.ADMIN.REVOKE_USER_ACCESS.DESCRIPTION', {
          userName: `${person.firstName} ${person.lastName}`,
        }),
      },
    })
    dialogRef
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true))
      .subscribe(async (confirmed) => {
        if (confirmed) {
          const wasAt = this.people.findIndex((other) => other.userId === person.userId)
          this.people = this.people.filter((other) => other.userId !== person.userId)
          try {
            await this.removeCallAccessMutation.mutateAsync({ callId: this.data.callId, userId: person.userId })
            this.toastService.show('CALLS.ADMIN.REVOKE_USER_ACCESS.SUCCESS')
          } catch {
            this.people = restoreCallUser(this.people, person, wasAt)
          }
        }
      })
  }
}
